import { vesselCardStyles } from "../constants/vessel-card-styles.constants";
import { twMerge } from "tailwind-merge";

const renderPlaceholder = (className: string) => (
  <span className={twMerge("block h-3 rounded bg-gray-100 dark:bg-gray-700", className)} />
);

// Keeps the card's panel layout visible before selection and while vessel data loads, without invented values or controls.
// The page owns loading announcements; decorative placeholders and motion respect assistive and reduced-motion settings.
const VesselCardSkeleton = () => (
  <div data-test="vessel-card-loading">
    <div
      aria-hidden="true"
      data-test="vessel-card-skeleton"
      className="grid grid-cols-1 gap-4 motion-safe:animate-pulse xl:grid-cols-12"
    >
      <section className={twMerge(vesselCardStyles.surface, "xl:col-span-9")}>
        <div className="mb-5 flex items-center gap-2">
          {renderPlaceholder("h-9 w-9 rounded-full bg-primary-10 dark:bg-teal-950/60")}
          <h2 className={vesselCardStyles.sectionTitle}>Performance evaluation</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-[1.2fr_0.85fr_1.15fr] md:divide-x md:divide-gray-200 dark:md:divide-gray-700">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {["basis", "evaluated", "fixture"].map((field) => (
              <div key={field} className="space-y-3">
                {renderPlaceholder("w-20 max-w-full")}
                {renderPlaceholder("h-4 w-24 max-w-full")}
              </div>
            ))}
          </div>
          <div className="space-y-3 md:pl-6">
            {renderPlaceholder("w-28 max-w-full")}
            <div className="flex gap-2">
              {renderPlaceholder("h-10 w-16")}
              {renderPlaceholder("h-10 w-16")}
            </div>
            {renderPlaceholder("w-full")}
            {renderPlaceholder("w-3/4")}
          </div>
          <div className="space-y-3 md:pl-6">
            {renderPlaceholder("w-32 max-w-full")}
            <div className="flex flex-wrap gap-2">
              {["imos", "input", "custom"].map((source) => (
                <span key={source} className="h-10 w-24 rounded bg-gray-100 dark:bg-gray-700" />
              ))}
            </div>
            {renderPlaceholder("w-full")}
            {renderPlaceholder("w-4/5")}
          </div>
        </div>
      </section>

      <section className={twMerge(vesselCardStyles.surface, "xl:col-span-3")}>
        <h2 className={twMerge(vesselCardStyles.sectionTitle, "mb-4")}>Vessel particulars</h2>
        <div className="space-y-2.5">
          {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="grid h-5 grid-cols-[0.85fr_1.15fr] items-center gap-3">
              {renderPlaceholder("w-3/4")}
              {renderPlaceholder(index % 2 === 0 ? "w-4/5" : "w-3/5")}
            </div>
          ))}
        </div>
      </section>

      <section className={twMerge(vesselCardStyles.surface, "min-w-0 xl:col-span-7")}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className={vesselCardStyles.sectionTitle}>Performance comparison</h2>
          {renderPlaceholder("h-9 w-32")}
        </div>
        <div className="mb-4 flex flex-wrap gap-2 rounded-md bg-gray-50 p-2 dark:bg-gray-900/50">
          {["cp", "baltic", "zn"].map((comparison) => (
            <span key={comparison} className="h-7 w-36 rounded bg-gray-100 dark:bg-gray-700" />
          ))}
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[740px]">
            <div className="grid grid-cols-[1.4fr_repeat(6,minmax(0,1fr))_1.4fr] items-center gap-5 py-4">
              {Array.from({ length: 8 }, (_, index) => (
                <span key={index} className="h-3 rounded bg-gray-100 dark:bg-gray-700" />
              ))}
            </div>
            {["ballast-eco", "laden-eco", "ballast-full", "laden-full"].map((mode) => (
              <div key={mode} className="grid grid-cols-[1.4fr_repeat(6,minmax(0,1fr))_1.4fr] items-center gap-5 py-4">
                {renderPlaceholder("h-4 w-4/5")}
                {Array.from({ length: 6 }, (_, index) => (
                  <span key={index} className="mx-auto h-3 w-8 rounded bg-gray-100 dark:bg-gray-700" />
                ))}
                {renderPlaceholder("w-4/5")}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={twMerge(vesselCardStyles.surface, "xl:col-span-2")}>
        <div className="mb-5 flex items-center gap-2">
          {renderPlaceholder("h-9 w-9 shrink-0 rounded-full bg-primary-10 dark:bg-teal-950/60")}
          <h2 className={vesselCardStyles.sectionTitle}>Current performance</h2>
        </div>
        {renderPlaceholder("h-4 w-full")}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          {["inspection", "dry-dock"].map((metric) => (
            <div
              key={metric}
              className="flex flex-col items-center gap-3 rounded-lg bg-gray-50 px-3 py-4 dark:bg-gray-900/50"
            >
              {renderPlaceholder("h-5 w-5")}
              {renderPlaceholder("w-4/5")}
              {renderPlaceholder("w-3/5")}
              {renderPlaceholder("mt-1 h-4 w-16")}
            </div>
          ))}
        </div>
      </section>

      <section className={twMerge(vesselCardStyles.surface, "self-start xl:col-span-3")}>
        <h2 className={twMerge(vesselCardStyles.sectionTitle, "mb-4")}>Engine &amp; design</h2>
        <div className="space-y-2.5">
          {Array.from({ length: 7 }, (_, index) => (
            <div key={index} className="grid h-5 grid-cols-[0.85fr_1.15fr] items-center gap-3">
              {renderPlaceholder("w-3/4")}
              {renderPlaceholder(index % 2 === 0 ? "w-4/5" : "w-3/5")}
            </div>
          ))}
        </div>
      </section>

      <div className={twMerge(vesselCardStyles.surface, "xl:col-span-9")}>
        {renderPlaceholder("h-4 w-56 max-w-full")}
      </div>
      <div className={twMerge(vesselCardStyles.surface, "xl:col-span-3")}>{renderPlaceholder("mx-auto h-4 w-24")}</div>

      {[
        { title: "TCO descriptions", columns: "xl:col-span-5" },
        { title: "DVP comments", columns: "xl:col-span-4" },
        { title: "RightShip", columns: "xl:col-span-3" },
      ].map((panel) => (
        <section key={panel.title} className={twMerge(vesselCardStyles.surface, panel.columns)}>
          <h2 className={twMerge(vesselCardStyles.sectionTitle, "mb-5")}>{panel.title}</h2>
          <div className="space-y-4">
            {renderPlaceholder("w-full")}
            {renderPlaceholder("w-4/5")}
            {renderPlaceholder("w-3/5")}
          </div>
        </section>
      ))}
    </div>
  </div>
);

export default VesselCardSkeleton;
